import {fromEvent, NEVER, race, Subject} from 'rxjs';
import {concat, filter, map, share, take, timeout} from 'rxjs/operators';
import uuid from 'uuid-random';

import {LegacyApplicationMessageModel} from '../../../models/applications/legacyApplicationMessageModel';
import {LegacyEntryPointTemplateModel} from '../../../models/applications/legacyEntryPointTemplateModel';
import {
  ApplicationCancelError,
  ApplicationCancelToken,
  ApplicationContext,
  Bridge,
  ContextFunctionCall,
  ContextFunctionCancel,
  EntryPointCreator,
  Initializer,
  View,
  WidgetTemplate,
} from './types/bridgeTypesV2';

/*
 * Constants.
 */

const promiseTimeout = 60 * 1000; // 60s.

/*
 * Enums.
 */

export enum ApplicationMessageTypesEnum {
  INITIALIZE = 'initialize',
  CONTEXT_UPDATE = 'context_update',
  FUNCTION_CALL = 'function_call',
  FUNCTION_CANCEL = 'function_cancel',
  FUNCTION_RESULT = 'function_result',
  FUNCTION_ERROR = 'function_error',
}

/*
 * Message types.
 */

export enum ApplicationResultMessageTypesEnum {
  CONTEXT = 'CONTEXT',
  FUNCTION_SUCCESS = 'FUNCTION_SUCCESS',
  FUNCTION_ERROR = 'FUNCTION_ERROR',
}

interface ApplicationContextResultMessage {
  type: ApplicationResultMessageTypesEnum.CONTEXT;
  context: ApplicationContext;
}

interface ApplicationFunctionSuccessResultMessage {
  type: ApplicationResultMessageTypesEnum.FUNCTION_SUCCESS;
  id: string;
  value: unknown;
}

interface ApplicationFunctionErrorResultMessage {
  type: ApplicationResultMessageTypesEnum.FUNCTION_ERROR;
  id: string;
  error: unknown;
}

type ApplicationFunctionSuccessErrorResultMessage =
  | ApplicationFunctionSuccessResultMessage
  | ApplicationFunctionErrorResultMessage;

export type ApplicationResultMessage =
  | ApplicationContextResultMessage
  | ApplicationFunctionSuccessErrorResultMessage;

function isApplicationContextResultMessage(
  message: ApplicationResultMessage,
): message is ApplicationContextResultMessage {
  return message.type === ApplicationResultMessageTypesEnum.CONTEXT;
}

function isApplicationFunctionResultMessage(
  message: ApplicationResultMessage,
): message is ApplicationFunctionSuccessErrorResultMessage {
  return (
    message.type === ApplicationResultMessageTypesEnum.FUNCTION_SUCCESS ||
    message.type === ApplicationResultMessageTypesEnum.FUNCTION_ERROR
  );
}

// The following types will be used by the new application entrypoints.

interface ApplicationFunctionContextUpdateMessage {
  type: ApplicationMessageTypesEnum.CONTEXT_UPDATE;
  context: ApplicationContext;
}

interface ApplicationFunctionResultMessage {
  type: ApplicationMessageTypesEnum.FUNCTION_RESULT;
  id: string;
  value: unknown;
}

interface ApplicationFunctionErrorMessage {
  type: ApplicationMessageTypesEnum.FUNCTION_ERROR;
  id: string;
  error: string;
}

export type ApplicationResultsMessage =
  | ApplicationFunctionContextUpdateMessage
  | ApplicationFunctionResultMessage
  | ApplicationFunctionErrorMessage;

/*
 * Bridge.
 */

export function buildApplicationBridgeV2(port: MessagePort): Bridge {
  /*
   * Communication with Front.
   */

  const messages = fromEvent<MessageEvent>(port, 'message').pipe(
    map((m) => m.data as ApplicationResultMessage),
    share(),
  );

  const proxyContextFunctionCall: ContextFunctionCall = (contextId, callId, name, args) => {
    // Serialize the function call and notify Front.
    const callMessage: LegacyApplicationMessageModel = {
      type: ApplicationMessageTypesEnum.FUNCTION_CALL,
      id: callId,
      contextId,
      call: {[name]: args},
    };
    port.postMessage(callMessage);

    return messages.pipe(
      filter(isApplicationFunctionResultMessage),
      filter((m) => m.id === callId),
      map((m) => {
        switch (m.type) {
          case ApplicationResultMessageTypesEnum.FUNCTION_SUCCESS:
            return m.value;

          case ApplicationResultMessageTypesEnum.FUNCTION_ERROR:
          default:
            throw new Error(String(m.error));
        }
      }),
      timeout(promiseTimeout),
      take(1),
    );
  };

  const proxyContextFunctionCancel: ContextFunctionCancel = (callId) => {
    const cancelMessage: LegacyApplicationMessageModel = {
      type: ApplicationMessageTypesEnum.FUNCTION_CANCEL,
      id: callId,
    };
    port.postMessage(cancelMessage);
  };

  /*
   * Widget view registration.
   */

  const viewUpdates = new Subject<View>();
  const registerView = (view: View) => {
    viewUpdates.next(view);
  };

  /*
   * Entry point registration.
   */

  let hasInitialized = false;
  const initialize = (initializer: Initializer) => {
    if (hasInitialized) {
      throw new Error('The app can be initialized only once.');
    }

    hasInitialized = true;

    // Build the entry-point collection.
    const entryPoints: Array<LegacyEntryPointTemplateModel> = [];
    const createEntryPoint: EntryPointCreator = (entryPoint: LegacyEntryPointTemplateModel) => {
      // Add it to the collection.
      entryPoints.push(entryPoint);

      // Listen for Context messages for this specific entryPoint.
      return messages.pipe(
        filter(isApplicationContextResultMessage),
        filter(({context}) => context.entryPointId === entryPoint.id),
        map(
          ({context}): any =>
            new Proxy(context, {
              get: (target, property) => {
                const functionArity = target.functionArities[String(property)];

                // If the property exists or it is not a function with arity, let the target handle it.
                if (property in target || !Number.isInteger(functionArity)) {
                  return (target as any)[property];
                }

                // Special case for the "createWidget" function.
                if (property === 'createWidget') {
                  return (widgetTemplate: WidgetTemplate, cancelToken?: ApplicationCancelToken) => {
                    const widgetId = widgetTemplate.id;
                    const callId = uuid();

                    return new Promise((resolve, reject) => {
                      const functionResult = proxyContextFunctionCall(context.id, callId, property, [
                        widgetTemplate,
                      ]).pipe(
                        filter(() => false), // We're only interested in errors.
                        concat(NEVER),
                      );

                      const viewUpdate = viewUpdates.pipe(
                        filter((v) => v.widgetId === widgetId),
                        timeout(promiseTimeout),
                        map((v) => ({...widgetTemplate, window: v.window})),
                      );

                      const subscription = race(functionResult, viewUpdate).subscribe({
                        next: resolve,
                        error: reject,
                      });

                      if (cancelToken) {
                        cancelToken.promise.then(() => {
                          // No need to do anything if the subscription was already closed.
                          if (subscription.closed) {
                            return;
                          }

                          // Otherwise, stop the subscription and reject the promise.
                          subscription.unsubscribe();
                          reject(new ApplicationCancelError());

                          proxyContextFunctionCancel(callId);
                        });
                      }
                    });
                  };
                }

                // Otherwise, create the function.
                return (...args: ReadonlyArray<any>) => {
                  const callId = uuid();
                  const propertyName = String(property);

                  // Extract the cancel token.
                  const cancelTokenIndex = functionArity - 1;
                  const cancelToken: ApplicationCancelToken = args[cancelTokenIndex];

                  // Do not send the cancel token in the proxied arguments.
                  const argsToSend = args.slice(0, cancelTokenIndex);

                  return new Promise((resolve, reject) => {
                    const subscription = proxyContextFunctionCall(
                      context.id,
                      callId,
                      propertyName,
                      argsToSend,
                    ).subscribe({
                      next: resolve,
                      error: reject,
                    });

                    if (cancelToken) {
                      cancelToken.promise.then(() => {
                        // No need to do anything if the subscription was already closed.
                        if (subscription.closed) {
                          return;
                        }

                        // Otherwise, stop the subscription and reject the promise.
                        subscription.unsubscribe();
                        reject(new ApplicationCancelError());

                        proxyContextFunctionCancel(callId);
                      });
                    }
                  });
                };
              },
            }),
        ),
      );
    };
    initializer(createEntryPoint);

    // Send the entry points to the app.
    const message: LegacyApplicationMessageModel = {
      type: ApplicationMessageTypesEnum.INITIALIZE,
      entryPoints,
    };
    port.postMessage(message);
  };

  /*
   * Resulting bridge.
   */

  return {
    proxyContextFunctionCall,
    proxyContextFunctionCancel,
    registerView,
    initialize,
  };
}
